import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../../store/index";

import ChatWindow from "./ChatWindow";
import {
  fetchWhatsappChats,
  fetchWhatsappChatsList,
  setSelectedChatIdSlice,
  updateChatIsSeenStatus,
} from "../../../store/slices/whatsappDataSlice";
import AllChat from "./AllChat";
import UnReadChat from "./unReadChats";

const ChatSkeleton: React.FC = () => (
  <div className="p-4 border-b border-gray-100 animate-pulse">
    <div className="flex items-center space-x-3">
      <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
      <div className="flex-1 min-w-0">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-3 bg-gray-200 rounded w-12 flex-shrink-0"></div>
    </div>
  </div>
);

// Multiple chat skeletons
const ChatListSkeleton: React.FC = () => (
  <div className="flex-1 overflow-y-auto">
    {[...Array(8)].map((_, index) => (
      <ChatSkeleton key={index} />
    ))}
  </div>
);

const ChatList: React.FC = () => {
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const [filterType, setFilterType] = useState("all");

  const [, setOpenChatId] = React.useState<string>("");
  const loading = useSelector(
    (state: RootState) => state.whatsAppData.chatListLoading
  );

  const [showChatList, setShowChatList] = useState(true);

  const chatList = useSelector(
    (state: RootState) => state.whatsAppData.whatsappChatList
  );

  const dispatch: RootDispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchWhatsappChatsList());
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobileView && selectedChatId) {
      setShowChatList(false);
    }
  }, [selectedChatId, isMobileView]);

  const selectedChat = chatList?.find((c) => c._id === selectedChatId);

  const handleBackToList = () => {
    setShowChatList(true);
  };

  const unReadChatsFilter = useMemo(() => {
    return chatList
      ?.filter((chat) => chat.receiver.includes(searchQuery))
      ?.filter((chat) => chat.unReadMessage > 0)
      .map((chat) => {
        return {
          ...chat,
          lastMessage: chat.latestMessage?.message || "",
          lastTimestamp: chat?.latestMessage?.createdAt || null,
          unReadMessage: chat.unReadMessage,
        };
      });
  }, [chatList, searchQuery]);

  const handleSelectChat = async (id: string) => {
    const chat = chatList?.find((chat) => chat?._id === id);
    if (!chat) return;

    if (isMobileView) setShowChatList(false);
    setSelectedChatId(id);
    setOpenChatId(id);
    dispatch(setSelectedChatIdSlice(id));

    await dispatch(fetchWhatsappChats({ chatId: chat?._id, page: 1 }));
    await dispatch(updateChatIsSeenStatus(chat._id));
  };

  useEffect(() => {
    unReadChatsFilter?.length === 0 && setFilterType("all");
  }, [unReadChatsFilter]);

  return (
    <div className="flex flex-col md:flex-row h-full bg-white">
      {(showChatList || !isMobileView) && (
        <div className="w-full md:w-1/3 lg:w-1/4 border-r border-gray-200 overflow-hidden flex flex-col bg-gray-50">
          <div className="p-4 bg-white border-b border-gray-200 shadow-sm">
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 pl-10 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 text-sm"
              />
              <svg
                className="w-5 h-5 absolute left-3 top-3.5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </div>

            <div className="flex rounded-xl bg-gray-100 p-1 shadow-inner">
              <button
                onClick={() => setFilterType("all")}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                  filterType === "all"
                    ? "bg-white text-green-600 shadow-md transform scale-[0.98]"
                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                }`}
              >
                All chats
              </button>
              <button
                onClick={() => setFilterType("unread")}
                className={`flex items-center justify-center gap-2 flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                  filterType === "unread"
                    ? "bg-white text-green-600 shadow-md transform scale-[0.98]"
                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                }`}
              >
                Unread
                {unReadChatsFilter?.length! > 0 && (
                  <span className="flex items-center justify-center h-5 w-5 bg-green-500 text-white text-xs font-bold rounded-full animate-pulse">
                    {unReadChatsFilter?.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Loading states */}
          {loading ? (
            <div className="flex-1 bg-white">
              <ChatListSkeleton />
            </div>
          ) : (
            <>
              {filterType === "all" ? (
                <div className="flex-1 overflow-y-auto bg-white">
                  <AllChat
                    searchQuery={searchQuery}
                    setSelectedChatId={setSelectedChatId}
                    handleSelectChat={handleSelectChat}
                    selectedChat={selectedChat}
                    selectedChatId={selectedChatId || ""}
                  />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto bg-white">
                  {unReadChatsFilter?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="text-gray-500 font-medium">
                        All caught up!
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        No unread messages
                      </p>
                    </div>
                  ) : (
                    <UnReadChat
                      unReadChatsFilter={unReadChatsFilter || []}
                      setSelectedChatId={setSelectedChatId}
                      handleSelectChat={handleSelectChat}
                      selectedChat={selectedChat}
                      selectedChatId={selectedChatId || ""}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {(!showChatList || !isMobileView) && (
        <div className="w-full md:w-2/3 lg:w-3/4 h-full bg-white">
          {selectedChat ? (
            <ChatWindow
              onBackClick={isMobileView ? handleBackToList : undefined}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 bg-gray-50">
              <div className="text-center max-w-md mx-auto p-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <svg
                    className="w-12 h-12 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-0.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
                <p className="text-gray-500 leading-relaxed">
                  Select a chat from the sidebar to start messaging. Your
                  conversations will appear here.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatList;
